import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Button from '../ui/Button';
import Input from '../ui/Input';
import AddressSearch from './AddressSearch';
import NewOrgModal from './NewOrgModal';
import type { GeocodeResult } from '../../services/geocode';
import organizationService from '../../services/organization';
import orchardService from '../../services/orchard';
import { GrowingRegionPicker } from '../cultivar';
import { byCountryThenName } from '../cultivar/growingRegionDraft';
import type { GrowingRegion } from '../../types/cultivar';
import { useAuth } from '../../context/AuthContext';
import { isOrgSuperAdmin } from '../../utils/auth';
import { isIOSApp } from '../../utils/platform';

const schema = z.object({
  name: z.string().min(1, 'Farm name is required').max(255),
  description: z.string().optional(),
  location: z.string().max(255).optional(),
  total_area_hectares: z.number().positive().optional(),
  // Nullable rather than optional: clearing the field has to be distinguishable
  // from not touching it, since the farm edit form sends every value.
  growing_region_id: z.number().nullable().optional(),
  // Blank is allowed: a new farm is then given a code from its name.
  code: z.string().trim().toUpperCase()
    .regex(/^[A-Z0-9]{2,6}$/, 'A farm code is 2 to 6 letters or numbers')
    .or(z.literal(''))
    .optional(),
  active: z.boolean().optional(),
});

type FarmFieldData = z.infer<typeof schema>;

interface FarmFormProps {
  initialValues?: Partial<FarmFieldData>;
  areaFromMap?: number | null;
  onSubmit: (data: FarmFieldData & { target_org_id?: number }) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  hasPolygon?: boolean;
  /** Centre of the drawn boundary, used to suggest the nearest growing region. */
  centerPoint?: { lat: number; lng: number } | null;
  /** Called when an address search result is selected so the parent can recenter the map. */
  onAddressSelected?: (result: GeocodeResult) => void;
  /** Called when the super-admin selects/creates a different target org. */
  onTargetOrgChanged?: (orgId: number | null) => void;
}

function FarmForm({
  initialValues,
  areaFromMap,
  onSubmit,
  onCancel,
  isSubmitting = false,
  hasPolygon = false,
  centerPoint,
  onAddressSelected,
  onTargetOrgChanged,
}: FarmFormProps) {
  const { user } = useAuth();
  const isSuperAdmin = isOrgSuperAdmin(user);

  const [orgs, setOrgs] = useState<{ id: number; name: string }[]>([]);
  const [targetOrgId, setTargetOrgId] = useState<number | null>(null);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [showNewOrgModal, setShowNewOrgModal] = useState(false);
  const [addressValue, setAddressValue] = useState(initialValues?.location || '');
  const [regions, setRegions] = useState<GrowingRegion[]>([]);
  const [nearest, setNearest] = useState<{ region: GrowingRegion; distance_km: number } | null>(null);
  /** What picking the address did to the region, said under the picker so it is never silent. */
  const [regionNote, setRegionNote] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FarmFieldData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initialValues?.name || '',
      description: initialValues?.description || '',
      location: initialValues?.location || '',
      total_area_hectares: initialValues?.total_area_hectares,
      growing_region_id: initialValues?.growing_region_id ?? null,
      code: initialValues?.code || '',
      active: initialValues?.active !== false,
    },
  });

  const currentArea = watch('total_area_hectares');
  const regionId = watch('growing_region_id') ?? null;

  useEffect(() => {
    if (areaFromMap == null || areaFromMap === currentArea) {
      return;
    }
    setValue('total_area_hectares', areaFromMap, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [areaFromMap, currentArea, setValue]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    setOrgsLoading(true);
    organizationService
      .listAll()
      .then((res) => {
        if (res.success && res.data) setOrgs(res.data);
      })
      .finally(() => setOrgsLoading(false));
  }, [isSuperAdmin]);

  useEffect(() => {
    let cancelled = false;
    orchardService.listGrowingRegions()
      .then((res) => {
        if (!cancelled && res.success && res.data) setRegions(res.data);
      })
      // The field is optional; a failure here must not block saving a farm.
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // A farm sits still, so its boundary is enough to say which region it is
  // probably in. The suggestion is offered, never applied: this database has no
  // PostGIS, so the match is nearest-centroid rather than point-in-polygon, and
  // a silently stamped region ends up averaged into the cultivar maturity it
  // feeds. Skipped once a region is chosen — nothing to suggest.
  useEffect(() => {
    if (regionId || !centerPoint) { setNearest(null); return; }
    let cancelled = false;
    orchardService.findNearestRegion(centerPoint.lat, centerPoint.lng)
      .then((res) => {
        if (!cancelled && res.success) setNearest(res.data ?? null);
      })
      .catch(() => { if (!cancelled) setNearest(null); });
    return () => { cancelled = true; };
  }, [regionId, centerPoint]);

  const handleOrgSelect = (v: string) => {
    if (v === '__new__') {
      setShowNewOrgModal(true);
      return;
    }
    const id = v === '' ? null : parseInt(v, 10);
    setTargetOrgId(id);
    onTargetOrgChanged?.(id);
  };

  const handleOrgCreated = (org: { id: number; name: string }) => {
    setOrgs((prev) => [...prev.filter((o) => o.id !== org.id), org].sort((a, b) => a.name.localeCompare(b.name)));
    setTargetOrgId(org.id);
    onTargetOrgChanged?.(org.id);
    setShowNewOrgModal(false);
  };

  const handleAddressChange = (v: string) => {
    setAddressValue(v);
    setValue('location', v, { shouldDirty: true });
  };

  const handleAddressPick = (r: GeocodeResult) => {
    setAddressValue(r.display_name);
    setValue('location', r.display_name, { shouldDirty: true });
    onAddressSelected?.(r);
    void applyRegionFromAddress(r);
  };

  // A Places pick knows where the farm is and which council it sits in, so the
  // region is set from it: the nearest one close by, else one made from the
  // address (Rob, 2026-10-01). Only while no region is chosen — a region
  // somebody picked is never replaced — and not for a farm being made in
  // another org, whose regions are not this user's to make.
  const applyRegionFromAddress = async (r: GeocodeResult) => {
    const place = r.place;
    if (!place || regionId || targetOrgId) return;
    try {
      const res = await orchardService.resolveGrowingRegion({
        latitude: r.lat,
        longitude: r.lon,
        area: place.area ?? null,
        locality: place.suburb,
        state: place.state,
        country: place.country,
        country_code: place.country_code ?? null,
      });
      const found = res.success ? res.data : null;
      if (!found) return;
      setRegions((prev) => prev.some((x) => x.id === found.region.id)
        ? prev
        : [...prev, found.region].sort(byCountryThenName));
      setValue('growing_region_id', found.region.id, { shouldDirty: true });
      setRegionNote(found.created
        ? `New region ${found.region.name} created from this address and applied.`
        : `Region set to ${found.region.name} from this address.`);
    } catch {
      // Optional field; the picker is still there.
    }
  };

  const submitWrapped = (data: FarmFieldData) => {
    onSubmit({ ...data, target_org_id: targetOrgId ?? undefined });
  };

  return (
    <>
      <form onSubmit={handleSubmit(submitWrapped)} className="space-y-4">
        {isSuperAdmin && (
          <div className="space-y-2 p-3 rounded-lg border border-primary-200 bg-primary-50">
            <label className="block text-sm font-semibold text-primary-900">
              Organisation {orgsLoading && <span className="text-xs text-primary-600">(loading…)</span>}
            </label>
            <select
              className="input-professional w-full"
              value={targetOrgId === null ? '' : String(targetOrgId)}
              onChange={(e) => handleOrgSelect(e.target.value)}
            >
              <option value="">— My organisation (default) —</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
              {/* Organisation creation is hidden on iOS (App Store 3.1.1) */}
              {!isIOSApp() && <option value="__new__">+ Create new organisation…</option>}
            </select>
            <p className="text-xs text-primary-700">
              Super admin: select the org this farm belongs to, or create a new one.
            </p>
          </div>
        )}

        <Input
          label="Farm Name *"
          placeholder="e.g., North Orchard"
          error={errors.name?.message}
          {...register('name')}
        />

        <div className="space-y-1">
          <Input
            label="Farm Code"
            placeholder="e.g., HOME"
            maxLength={6}
            autoCapitalize="characters"
            className="uppercase"
            error={errors.code?.message}
            {...register('code')}
          />
          <p className="text-xs text-neutral-500 break-words">
            2 to 6 letters or numbers. This farm&rsquo;s purchase orders are numbered with it, such as
            PO-HOME-1-001.{' '}
            {initialValues?.code
              ? 'Changing it only affects orders raised from now on.'
              : 'Leave it blank and one is made from the farm name.'}
          </p>
        </div>

        <AddressSearch
          value={addressValue}
          onChange={handleAddressChange}
          onSelect={handleAddressPick}
          placeholder="e.g., 123 Orchard Rd, Harcourt VIC"
        />

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-neutral-700">Description</label>
          <textarea
            className="input-professional w-full resize-none"
            rows={3}
            placeholder="Optional notes about this farm..."
            {...register('description')}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-neutral-700">
            Total Area (hectares)
          </label>
          <input
            type="number"
            step="0.01"
            className="input-professional w-full bg-neutral-50"
            placeholder="Auto-calculated from polygon"
            {...register('total_area_hectares', { valueAsNumber: true })}
          />
          {areaFromMap != null && (
            <p className="text-xs text-success-600">
              ✓ Area calculated from drawn polygon
            </p>
          )}
        </div>

        <div className="space-y-2">
          <GrowingRegionPicker
            regions={regions}
            value={regionId ?? 0}
            onChange={(id) => { setValue('growing_region_id', id || null, { shouldDirty: true }); setRegionNote(null); }}
            onCreated={(r) => setRegions((prev) => [...prev, r].sort(byCountryThenName))}
            createRegion={(payload) => orchardService.createGrowingRegion(payload)}
            label="Growing region"
          />
          {regionNote && regionId && (
            <p className="text-xs text-success-700">{regionNote} Change it above if it is wrong.</p>
          )}
          <p className="text-xs text-neutral-500">
            The climate area this farm sits in — Goulburn Valley, Batlow. Not a state or a
            licensing territory. Evaluations recorded here inherit it, which is what lets
            their maturity dates count towards a cultivar's harvest timing.
          </p>
          {nearest && (
            <button
              type="button"
              onClick={() => setValue('growing_region_id', nearest.region.id, { shouldDirty: true })}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium text-left"
            >
              Nearest region to this boundary is {nearest.region.name} ({nearest.distance_km} km away) — use it
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="active"
            className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            {...register('active')}
            defaultChecked
          />
          <label htmlFor="active" className="text-sm text-neutral-700">Active farm</label>
        </div>
        {/* Archiving through the form releases the same hardware as the farm page's Archive. */}
        {initialValues?.active !== false && initialValues?.name && watch('active') === false && (
          <p className="text-xs text-amber-700">
            Saving archives this farm and releases its irrigation bridges, soil sensors and the relay links on its
            network map, so they can be used on another farm. Reactivating it later does not reattach them.
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            disabled={!hasPolygon}
          >
            Save Farm
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>

        {!hasPolygon && (
          <p className="text-xs text-amber-600">
            Draw a farm boundary on the map before saving
          </p>
        )}
      </form>

      <NewOrgModal
        open={showNewOrgModal}
        onClose={() => setShowNewOrgModal(false)}
        onCreated={handleOrgCreated}
      />
    </>
  );
}

export default FarmForm;
